/**
 * colorTransform — DTCG / legacy renk değerlerini deterministik CSS renk string'ine çevirir.
 *
 * Saf fonksiyonlar. Yan etki yok, I/O yok, global state yok.
 *
 * Desteklenen girdi biçimleri:
 *   1. HSL-bare legacy (shadcn):            `"221.2 83.2% 53.3%"`
 *   2. Hex:                                 `"#1A73E8"` / `"#fff"`
 *   3. W3C DTCG color objesi:               `{ colorSpace, components, hex?, alpha? }`
 *   4. CSS renk fonksiyonu string'i:        `"hsl(...)"`, `"oklch(...)"`, `"rgb(...)"`
 *
 * Hedefler:
 *   - `oklch`  → culori ile sRGB/hex/HSL → OKLCH; sabit precision (deterministik).
 *   - `hsl`    → HSL-bare legacy değer AYNEN korunur (passthrough); diğer girdiler hsl-bare'e çevrilir.
 */
import { converter, parse } from 'culori';
/** HSL-bare legacy biçimi: `"<H> <S>% <L>%"` (opsiyonel alfa `/ <A>`). */
const HSL_BARE_RE = /^-?\d*\.?\d+\s+-?\d*\.?\d+%\s+-?\d*\.?\d+%(\s*\/\s*[\d.]+%?)?$/;
const toOklch = converter('oklch');
const toHsl = converter('hsl');
/** culori float'larını kararlı stringe çevirir; bilimsel gösterim ve `-0` üretmez. */
function round(n, decimals) {
    if (n === undefined || Number.isNaN(n))
        return 0;
    const f = 10 ** decimals;
    const r = Math.round(n * f) / f;
    return Object.is(r, -0) ? 0 : r;
}
/** Bir değerin HSL-bare legacy biçiminde olup olmadığını söyler. */
export function isHslBare(value) {
    return HSL_BARE_RE.test(value.trim());
}
/** HSL-bare → culori'nin parse edebileceği `hsl(...)` fonksiyon string'i. */
function hslBareToCss(value) {
    const v = value.trim();
    const slash = v.indexOf('/');
    if (slash !== -1) {
        const body = v.slice(0, slash).trim().replace(/\s+/g, ' ');
        const alpha = v.slice(slash + 1).trim();
        return `hsl(${body} / ${alpha})`;
    }
    return `hsl(${v.replace(/\s+/g, ' ')})`;
}
/**
 * DTCG color objesini culori Color'a çevirir.
 * Strateji: önce `hex` (en taşınabilir), yoksa sRGB components, en son colorSpace+components generic.
 */
function dtcgColorToCulori(c) {
    if (typeof c.hex === 'string' && c.hex.length > 0) {
        const parsed = parse(c.hex);
        if (parsed)
            return c.alpha !== undefined ? { ...parsed, alpha: c.alpha } : parsed;
    }
    const comps = c.components;
    if (comps && comps.length >= 3) {
        const space = (c.colorSpace ?? 'srgb').toLowerCase();
        if (space === 'srgb' || space === 'rgb') {
            const color = { mode: 'rgb', r: comps[0], g: comps[1], b: comps[2] };
            if (c.alpha !== undefined)
                color.alpha = c.alpha;
            return color;
        }
    }
    return undefined;
}
/** Girdiyi (string ya da DTCG obje) normalize edip culori Color'a çevirir. */
function toCulori(input) {
    if (typeof input === 'object')
        return dtcgColorToCulori(input);
    const value = input.trim();
    if (isHslBare(value))
        return parse(hslBareToCss(value)) ?? undefined;
    return parse(value) ?? undefined;
}
/** OKLCH Color → deterministik `oklch(L C H)` (+ alpha) string. Achromatik hue → 0. */
function formatOklch(c) {
    // converter('oklch') daima { mode:'oklch', l, c, h?, alpha? } döner.
    const o = c;
    const l = round(o.l, 4);
    const chroma = round(o.c, 4);
    // Achromatik renklerde culori h = undefined ('none'); kararlı token için 0'a sabitle.
    const hue = round(o.h, 2);
    const base = `oklch(${l} ${chroma} ${hue})`;
    if (o.alpha !== undefined && o.alpha < 1) {
        return `oklch(${l} ${chroma} ${hue} / ${round(o.alpha, 4)})`;
    }
    return base;
}
/** HSL Color → HSL-bare legacy `"<H> <S>% <L>%"` (+ `/ <A>`). */
function formatHslBare(c) {
    const h = c;
    const hue = round(h.h, 1);
    const sat = round((h.s ?? 0) * 100, 1);
    const lit = round((h.l ?? 0) * 100, 1);
    const base = `${hue} ${sat}% ${lit}%`;
    if (h.alpha !== undefined && h.alpha < 1) {
        return `${base} / ${round(h.alpha * 100, 1)}%`;
    }
    return base;
}
/**
 * Tek bir renk değerini hedef biçime çevirir.
 *
 * @param input  HSL-bare string, hex string, CSS renk fonksiyonu string'i ya da DTCG color objesi.
 * @param target 'oklch' (varsayılan) veya 'hsl' (HSL-bare legacy korunur/üretilir).
 * @returns      Transform edilmiş CSS değer string'i, ya da girdi parse edilemezse `undefined`.
 */
export function transformColor(input, target = 'oklch') {
    // HSL hedefinde, zaten HSL-bare olan legacy değeri AYNEN koru (lossless passthrough).
    if (target === 'hsl' && typeof input === 'string' && isHslBare(input)) {
        return input.trim().replace(/\s+/g, ' ');
    }
    const color = toCulori(input);
    if (!color)
        return undefined;
    if (target === 'hsl') {
        const hsl = toHsl(color);
        return hsl ? formatHslBare(hsl) : undefined;
    }
    const oklch = toOklch(color);
    return oklch ? formatOklch(oklch) : undefined;
}
//# sourceMappingURL=colorTransform.js.map